import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "md_concierge_session";

const WELCOME = "Bienvenue à la MAISON DOUBA. Je suis votre concierge olfactif. Dites-moi ce que vous cherchez : une occasion, une note préférée, une émotion à faire naître — je vous guiderai vers votre signature.";

/** Parse a message that may contain [label](url) markdown links into React children. */
function renderWithLinks(text) {
  if (!text) return null;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const [, label, url] = m;
    const isInternal = url.startsWith("/");
    parts.push(
      isInternal ? (
        <Link key={`${m.index}-l`} to={url} className="text-[color:var(--gold)] underline underline-offset-2 hover:text-white">
          {label}
        </Link>
      ) : (
        <a key={`${m.index}-a`} href={url} target="_blank" rel="noreferrer" className="text-[color:var(--gold)] underline underline-offset-2">
          {label}
        </a>
      )
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Onboarding auto-open : new visitors on the homepage after 30 seconds
  useEffect(() => {
    if (open) return;
    // Only trigger on the homepage
    if (window.location.pathname !== "/") return;
    // Only for visitors who never engaged with the concierge
    try {
      if (localStorage.getItem("md_concierge_onboarded")) return;
      if (localStorage.getItem(SESSION_KEY)) return; // already had a session
    } catch { /* noop */ }
    const t = setTimeout(() => {
      setOpen(true);
      try { localStorage.setItem("md_concierge_onboarded", "1"); } catch { /* noop */ }
      // Prepend a friendly onboarding line only if the conversation is still fresh
      setMessages((prev) => (
        prev.length <= 1
          ? [{ role: "assistant", content: "Bienvenue chez MAISON DOUBA. Je suis votre concierge olfactif. Cherchez-vous un parfum pour vous, un cadeau, une occasion précise ? Décrivez-moi ce que vous aimez, je m'occupe du reste." }]
          : prev
      ));
    }, 30000);
    return () => clearTimeout(t);
  }, [open]);

  // Hydrate session from localStorage + load history
  useEffect(() => {
    try {
      const sid = localStorage.getItem(SESSION_KEY);
      if (sid) {
        setSessionId(sid);
        fetch(`${API}/ai/concierge/history/${sid}`)
          .then((r) => r.ok ? r.json() : [])
          .then((rows) => {
            if (rows && rows.length > 0) {
              setMessages([{ role: "assistant", content: WELCOME }, ...rows]);
            }
          })
          .catch((err) => console.debug("[ChatWidget] history fetch failed:", err?.message || err));
      }
    } catch (e) {
      console.debug("[ChatWidget] localStorage access failed:", e?.message || e);
    }
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "", pending: true }]);
    setInput("");
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API}/ai/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) throw new Error("HTTP " + resp.status);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Read SSE events: `data: {...}\n\n`
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Split into complete SSE events
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const event = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!event.startsWith("data:")) continue;
          try {
            const data = JSON.parse(event.slice(5).trim());
            if (data.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, content: (last.content || "") + data.delta, pending: false };
                }
                return copy;
              });
            } else if (data.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: data.error };
                return copy;
              });
            } else if (data.done) {
              if (data.session_id && !sessionId) {
                setSessionId(data.session_id);
                try { localStorage.setItem(SESSION_KEY, data.session_id); } catch {}
              }
            }
          } catch { /* ignore malformed */ }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: "Notre concierge est momentanément indisponible. Réessayez dans un instant." };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const resetConversation = () => {
    if (streaming) abortRef.current?.abort();
    try { localStorage.removeItem(SESSION_KEY); } catch {}
    setSessionId(null);
    setMessages([{ role: "assistant", content: WELCOME }]);
  };

  return (
    <>
      <button
        data-testid="chat-open-btn"
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 bg-[color:var(--gold)] text-black rounded-full flex items-center justify-center hover:bg-[color:var(--gold-hover)] transition-all shadow-lg ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        aria-label="Ouvrir le concierge"
      >
        <MessageCircle size={22} strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid="chat-widget"
          className="fixed bottom-6 right-6 z-50 w-[94vw] max-w-md h-[600px] max-h-[85vh] bg-[#0A0A0A] border border-[color:var(--gold)]/30 flex flex-col shadow-2xl"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[color:var(--gold)]/10 border border-[color:var(--gold)]/40 flex items-center justify-center">
                <Sparkles size={14} className="text-[color:var(--gold)]" />
              </div>
              <div>
                <p className="label-caps text-[color:var(--gold)] text-[9px] tracking-[0.25em]">Concierge olfactif · IA</p>
                <p className="font-serif text-white text-base leading-tight">MAISON DOUBA</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 1 && (
                <button
                  data-testid="chat-reset-btn"
                  onClick={resetConversation}
                  className="text-white/40 hover:text-white/80 text-[9px] label-caps tracking-[0.2em] px-2 py-1"
                  title="Nouvelle conversation"
                >
                  Reset
                </button>
              )}
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1" aria-label="Fermer">
                <X size={16} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${i}`}
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[color:var(--gold)] text-black"
                    : "bg-white/[0.04] text-white/90 border border-white/10"
                }`}
              >
                {m.pending && !m.content ? (
                  <span className="flex items-center gap-2 text-white/50">
                    <Loader2 size={12} className="animate-spin" />
                    <span className="label-caps text-[10px] tracking-[0.25em]">Réflexion…</span>
                  </span>
                ) : m.role === "assistant" ? (
                  <span className="whitespace-pre-wrap">{renderWithLinks(m.content)}</span>
                ) : (
                  <span className="whitespace-pre-wrap">{m.content}</span>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-white/10 flex items-center gap-2 p-3 bg-black">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Parlez-moi de vos envies olfactives…"
              className="flex-1 bg-transparent text-white placeholder:text-white/30 text-sm outline-none px-2 py-2"
              disabled={streaming}
              maxLength={1500}
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={!input.trim() || streaming}
              className="w-9 h-9 bg-[color:var(--gold)] text-black hover:bg-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Envoyer"
            >
              {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
          <p className="text-[9px] text-white/25 text-center py-1.5 label-caps tracking-[0.2em] bg-black">
            Propulsé par IA — vos échanges restent confidentiels
          </p>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
